import { BlockError } from '@/blocks.04-project/block-error/block-error';
import tab from '@/blocks.04-project/tabs/tabs.tmpl-specs/row-flex.module';
import cardSmall from '@/blocks.04-project/product-card/product-card.tmpl-specs/small.module';
import sliderNavigation from '@/blocks.04-project/slider-navigation/slider-navigation.tmpl-specs/base.module';
import toArray from 'lodash-es/toArray';
import forEach from 'lodash-es/forEach';
import get from 'lodash-es/get';
import map from 'lodash-es/map';
import find from 'lodash-es/find';
import filter from 'lodash-es/filter';
import set from 'lodash-es/set';
import isEqual from 'lodash-es/isEqual';
import some from 'lodash-es/some';
import values from 'lodash-es/values';
import join from 'lodash-es/join';
import isNull from 'lodash-es/isNull.js';
import includes from 'lodash-es/includes';
import without from 'lodash-es/without';
import toNumber from 'lodash-es/toNumber';
import keys from 'lodash-es/keys';
import isEmpty from 'lodash-es/isEmpty';
import reduce from 'lodash-es/reduce';

/***
 * Класс страницы сравнения
 * @example
 * Классы:
 * .share-active - если ссылка на "поделиться" была скопирована
 *
 * Аттрибуты на элементах:
 * .tabs__item[data-id] - {string} - id секции с товарами
 * .product-control[data-id] - {string} - id товара
 * .comparison__tab-pane[data-section-id] - {string} - id секции
 * [data-tab-count] - {number} - контейнер для количества товаров в каждом табе
 * [data-only-different] - {string} - чекбокс с id секции для переключения вида спецификаций
 * [data-clear-all] - {string} - кнопка с id секции для переключения очистки секции
 * [data-share] - {boolean} - кнопка поделиться сравнением
 * [data-control-compare] - {boolean} - кнопка добавить/удалить в сравнения
 * [data-control-compare] - {boolean} - кнопка добавить/удалить в сравнения
 * [data-rendered] - {boolean} - показывает какой таб уже был прорисован
 */
class Comparison {
  /***
   * Конструктор класса
   * @param elem {HTMLElement} DOM элемент формы с классом comparison
   * @param data {Object} данные для рендера
   */
  constructor(elem, data) {
    this.BlockError = new BlockError({ blockName: 'Comparison', destroy: this.destroy });
    if (!elem) return this.BlockError.error('Отсутствует elem');

    this.block = elem;
    this.block.Comparison = this;

		this.data = data || [];

		window.getBH((bh) => {
			this.bh = bh;
			this.init();
		});
  }

  init = async () => {
		this.setOptions();
		await this.loading(this.block, this.render);

		this.tabsContainer.addEventListener('shown.bs.tab', this.showTabHandler);
		this.block.addEventListener('removeCompare.iv.productControl', this.removeCompareHandler);
		this.block.addEventListener('change', this.changeHandler);
		this.block.addEventListener('click', this.clickHandler);
		if (this.isFake) this.block.addEventListener('click', this.captureClickHandler, { capture: true });

		this.block.classList.add('comparison_inited');
  };

	showTabHandler = (event) => {
		const id = event.target.dataset['id'];
		id && this.renderActiveTab(id);
	};

	removeCompareHandler = ({ detail }) => {
		this.loading(this.block, () => {
			const offerId = toNumber(get(detail, 'offerId', null));
			offerId && this.removeOffers([offerId]);
		});
	};

	changeHandler = (event) => {
		const onlyDifferent = event.target.closest('[data-only-different]');
		if (onlyDifferent) {
			const activeTabContainer = onlyDifferent.closest('.comparison__tab-pane');
			const sectionId = onlyDifferent.dataset['onlyDifferent'];
			this.isOnlyDifferent = event.target.checked;
			localStorage.setItem('comparisonOnlyDifferent', this.isOnlyDifferent);

			this.loading(activeTabContainer, () => {
				this.renderSpecs({ sectionId, different: this.isOnlyDifferent });
				this.reInitSpecsSlider(activeTabContainer, sectionId);
			});
		}
	};

	clickHandler = (event) => {
		const clearAll = event.target.closest('[data-clear-all]');
		if (clearAll) {
			const sectionId = clearAll.dataset['clearAll'];
			this.clearAll(sectionId);
			return;
		}

		const share = event.target.closest('[data-share]');
		if (share) {
			this.share();
			return;
		}
	};

	captureClickHandler = (event) => {
		if (event.target.matches('[data-control-compare]')) {
			event.stopPropagation();
			const offerId = toNumber(event.target.closest('[data-id]')?.dataset['id']);
			offerId && this.removeOffers([offerId]);
		}
	};

	clearAll = (sectionId) => {
		const section = find(this.sections, { id: sectionId });
		const offers = get(section, 'offers', []);

		this.loading(this.block, async () => {
			if (this.isFake) {
				this.removeOffers(offers);
				delete this.sectionsSliders[sectionId];
				return;
			}

			try {
				await window.Block.Compare.remove({ offerIds: offers });
				this.removeOffers(offers);
				delete this.sectionsSliders[sectionId];
			} catch (err) {
				console.error(err);
			}
		});
	};

	share = async () => {
		try {
			const url = new URL(this.shareLink);
			url.searchParams.append('productList', keys(this.offers).toString());

			await navigator.clipboard.writeText(url.toString());
			if (this.timer) clearTimeout(this.timer);
			else this.block.classList.add('share-active');

			this.timer = setTimeout(() => {
				this.block.classList.remove('share-active');
				this.timer = null;
			}, 1000);
		} catch {
			window.alert(window.BX?.message('comparisonShareError') || 'Не удалось скопировать ссылку');
		}
	};

	render = async () => {
		await this.renderTabs();
		this.renderTabsContent();
		await this.renderActiveTab(get(this.sections[0], 'id'));
	};

	renderTabs = async () => {
		this.tabsContainer.innerHTML = '';
		this.tabsContainer.insertAdjacentHTML('afterbegin', this.bh.apply([
			tab({
				data: map(this.sections, ({ id, text, offers }) => ({
					tabAttrs: {'data-id': id},
					title: `${text} <span class="text-secondary" data-tab-count>${offers.length}<span`,
					target: `#COMPARISON_TAB_${id}`
        })),
			})
		]));

		window.Block.Tabs.init(this.tabsContainer, true);
		const tabs = this.tabsContainer.querySelector('.tabs');
		if (!tabs) return this.BlockError.error('Отсутствует .tabs');

    this.tabList = this.tabsContainer.querySelector('.tabs__list');
    if (!this.tabList) return this.BlockError.error('Отсутствует .tabs__list');

    this.tabIns = await tabs.AsyncTabs;
	};

	renderTabsContent = () => {
		this.tabContentContainer.innerHTML = '';
		this.tabContentContainer.insertAdjacentHTML('afterbegin', this.bh.apply([
			map(this.sections, ({ id }, idx) => [
				{block: 'comparison', elem: 'tab-pane', cls: `tab-pane fade ${idx === 0 ? 'show active' : ''}`,
					attrs: {id: `COMPARISON_TAB_${id}`, 'data-section-id': id},
					content: [
						{elem: 'filter', content: [
								{block: 'form-check', cls: 'form-switch', props: { checked: this.isOnlyDifferent }, attrs: {'data-only-different': id},
									content: window.BX?.message('comparisonOnlyDifferent') || 'Показать только различия'
								},
								{tag: 'button', cls: 'a d-flex align-items-center',
									attrs: {'data-clear-all': id},
									content: [
										{block: 'fi', cls: 'fs-18 me-4', mods: {icon: 'trash'}},
										window.BX?.message('comparisonClearAll') || 'Очистить список',
									]},
							]},
						{elem: 'body', content: [
								{elem: 'loading-tab', content: [
										{elem: 'plug-cards', content: [...new Array(6)].map(() => [
												{elem: 'plug', content: [
														{elem: 'plug-header', content: [
																{elem: 'plug-image', cls: 'skeleton'},
																{elem: 'plug-actions', cls: 'skeleton'},
															]},
														{elem: 'plug-title', cls: 'skeleton'},
														{elem: 'plug-price', cls: 'skeleton'},
														{elem: 'plug-control', cls: 'skeleton'}
													]},
											])},
										{elem: 'loading-specs', content: [...new Array(5)].map(() => [
												{elem: 'specs-item', cls: 'skeleton'},
											])}
									]},
							]},
					]},
			]),
		]));
	};

	renderActiveTab = async (id) => {
		const activeTabContainer = this.block.querySelector(`.comparison__tab-pane[data-section-id="${id}"]:not([data-rendered])`);
		const bodyContainer = activeTabContainer?.querySelector('.comparison__body');
		if (!activeTabContainer || !bodyContainer) return;

		await this.loading(activeTabContainer, () => {
			const different = activeTabContainer.querySelector('[data-only-different] input')?.checked;
			const activeId = activeTabContainer.dataset['sectionId'] || '';
			const offers = get(find(this.sections, { 'id': activeId }), 'offers', []);
			const resultOffers = map(offers, (offerID) => get(this.offers, offerID));
			const sectionSpecs = this.getSectionSpecs({ offers: resultOffers, sectionId: activeId }) || [];
			const differentSectionSpecs = this.getDifferentSpecs(sectionSpecs) || [];

			set(this.sectionsSpecs, [activeId], {
				sectionSpecs,
				differentSectionSpecs,
			});

			this.renderOffers({ container: bodyContainer, resultOffers });
			this.renderSpecs({ sectionId: activeId, different });
			this.initSliders({ activeTabContent: activeTabContainer, sectionId: activeId });
			activeTabContainer.setAttribute('data-rendered', '');
		});
	};

	renderOffers = ({ container, resultOffers }) => {
		container.innerHTML = '';
		container.insertAdjacentHTML('afterbegin', this.bh.apply([
			{block: 'comparison', elem: 'products', content: [
					{cls: 'swiper', content: [
							{cls: 'swiper-wrapper', content: map(resultOffers, (offer) => [
									{cls: 'swiper-slide', content: [
											cardSmall({ src: offer.photos[0].src, attrs: {'data-id': offer.id} })
										]},
								])},
              sliderNavigation(),
						]},
				]},
			{block: 'comparison', elem: 'specs'}
		]));

		forEach(resultOffers, (offer) => {
			const cards = toArray(this.block.querySelectorAll(`[data-id="${offer.id}"]`));

			forEach(cards, (card) => {
				new window.Block.ProductCard({
					options: {
						data: {
							currentOfferId: get(offer, 'id'),
							title: get(offer, 'title'),
							description: get(offer, 'description'),
							vat: get(offer, 'vat'),
							offers: [offer],
              priceOnRequest: get(offer, 'priceOnRequest'),
              priceRequestFormId: get(offer, 'priceRequestFormId'),
						}
					},
					elem: card,
				});

				if (NOT_PROD) { // Добавление товара в сравнение только локально
					if (window.Block.Compare.includes({ offerId: offer.id })) return;
					(async () => {
						const ins = await card.AsyncProductCard;
						ins.updateCompare();
					})();
				}
			});
		});
	};

	renderSpecs = ({ sectionId, different = false }) => {
		const specsContainer = this.block.querySelector(`.comparison__tab-pane[data-section-id="${sectionId}"] .comparison__specs`);
		const specVariant = different ? 'differentSectionSpecs' : 'sectionSpecs';
		const sectionsSpecs = get(this.sectionsSpecs, [sectionId, specVariant], []);

		if (!specsContainer) return;

		specsContainer.innerHTML = '';
		specsContainer.insertAdjacentHTML('afterbegin', this.bh.apply([
      sectionsSpecs.map(({ delimiter, specs }) => [
        delimiter && !isEmpty(specs) && {block: 'comparison', elem: 'specs-item', content: [
            {elem: 'specs-delimiter', content: delimiter}
          ]},
        map(specs, ({ title, values }) => [
          {block: 'comparison', elem: 'specs-item', content: [
            {elem: 'specs-title', content: title},
            {elem: 'specs-list', content: [
                {cls: 'swiper', content: [
                    {cls: 'swiper-wrapper', content: map(values, ({ type, value }) => {
                        let content;
                        switch (type) {
                          case 'string':
                            content = [value];
                            break;
                          case 'array':
                            content = join(value, ' | ');
                            break;
                          case 'boolean':
                            content = value ? [
                              {block: 'fi', cls: 'fs-20 text-success', mods: {icon: 'check'}},
                            ] : [
                              {block: 'fi', cls: 'fs-20 text-danger', mods: {icon: 'minus-big'}},
                            ];
                            break;
                        }

                        return [
                          {cls: 'swiper-slide', content: [
                              {elem: 'spec-content', content: [
                                  isNull(value) && {block: 'fi', cls: 'fs-20 text-danger', mods: {icon: 'minus-big'}},
                                  !isNull(value) && content,
                                ]},
                            ]},
                        ];
                      })},
                  ]},
              ]}
				]},])
			]),
		]));
	};

	initSliders = async ({ activeTabContent, sectionId }) => {
		const { Swiper, Modules } = await Promise.all([
			import(/* webpackChunkName: "Swiper" */ 'swiper'),
			import(
				/* webpackChunkName: "SwiperModules" */
				/* webpackExports: ['Navigation', 'Controller'] */
				'swiper/modules')
		]).then((data) => {
			return {
				Swiper: data[0]?.Swiper,
				Modules: toArray(data[1]),
			};
		}).catch((err) => {
			throw new Error(err);
		});
		this.Swiper = Swiper;

		const sliderContainer = activeTabContent.querySelector('.comparison__products .swiper');
    const navNext = sliderContainer.querySelector('.swiper-button-next');
    const navPrev = sliderContainer.querySelector('.swiper-button-prev');

		const specsSwipers = this.getSpecsSliders({ container: activeTabContent });

		const productsSwiper = new Swiper(sliderContainer, {
			slidesPerView: 2,
			spaceBetween: 10,
			breakpoints: {
				868: { slidesPerView: 3 },
				992: { slidesPerView: 4 },
				1200: { slidesPerView: 5 },
				1440: { slidesPerView: 6 },
			},
			controller: {
				by: 'container',
				control: specsSwipers,
			},
            navigation: {
                nextEl: navNext,
                prevEl: navPrev,
            },
            on: {
                afterInit: (swiper) => this.block.dispatchEvent(new CustomEvent('iv.swiper.inited', { bubbles: true, detail: swiper })),
            },
			modules: Modules,
		});

		this.sectionsSliders[sectionId] = {
			productsSwiper,
		};
	};

	reInitSpecsSlider = (container, sectionId) => {
		const specsSwipers = this.getSpecsSliders({ container });

		this.sectionsSliders[sectionId].productsSwiper.controller.control = [...specsSwipers];
		this.sectionsSliders[sectionId].productsSwiper.update();
	};

	removeOffers = (offersIds) => {
		forEach(offersIds, (id) => {
			delete this.offers[id];
		});

		const emptySectionsIds = [];
		forEach(this.sections, (item) => {
			item.offers = without(item.offers, ...offersIds);
			if (!item.offers.length) emptySectionsIds.push(item.id);
		});

		this.sections = filter(this.sections, ({ id }) => !includes(emptySectionsIds, id));
		this.removeSections(emptySectionsIds);
		forEach(this.sections, ({ id, offers }) => {
			const countContainer = this.tabsContainer.querySelector(`[data-id="${id}"] [data-tab-count]`);
			if (countContainer) countContainer.innerHTML = offers.length;
		});

		this.data.offers = this.offers;
		this.data.sections = this.sections;

		this.loading(null, () => {
			forEach(toArray(this.block.querySelectorAll('[data-rendered]')), (item) => {
				item.removeAttribute('data-rendered');
				if (item.classList.contains('active')) {
					this.renderActiveTab(item.dataset['sectionId']);
				}
			});
		});
	};

	/***
	 * Удаление секций
	 * @param sectionIds{String[]} - id секций для удаления
	 */
	removeSections = (sectionIds) => {
		if (!this.tabIns) return;

    let isSlideRemoved = false;
    forEach(sectionIds, (sectionId) => {
      const sectionTab = this.tabsContainer.querySelector(`[data-id="${sectionId}"]`);
      if (sectionTab) {
        sectionTab.remove();
        isSlideRemoved = true;
      }
    });

    forEach(sectionIds, (sectionId) => {
      const sectionTabContainer = this.block.querySelector(`#COMPARISON_TAB_${sectionId}`);
      if (sectionTabContainer) sectionTabContainer.remove();
    });

    const tabListChildren = get(this.tabList, ['firstChild', 'children'], []);
    if (tabListChildren.length) {
      if (!isSlideRemoved) return;

      const tab = tabListChildren[0];
      tab && window.bootstrap.Tab.getOrCreateInstance(tab).show();
    } else {
      location.reload();
    }
	};

	setOptions = () => {
		this.shareLink = this.block.dataset['shareLink'] || '';
		this.isOnlyDifferent = JSON.parse(localStorage.getItem('comparisonOnlyDifferent')) || false;

		this.sectionsSpecs = {};
		this.sectionsSliders = {};
		this.isFake = get(this.data, 'isFake', false);
		this.sections = get(this.data, 'sections', []);
		this.offers = get(this.data, 'offers', []);

		this.tabsContainer = this.block.querySelector('.comparison__tabs');
		if (!this.tabsContainer) return this.BlockError.error('Отсутствует .comparison__tabs');

		this.tabContentContainer = this.block.querySelector('.comparison__tab-content');
		if (!this.tabContentContainer) return this.BlockError.error('Отсутствует .comparison__tab-content');
	};

	getSpecsSliders = ({ container }) => {
		const specsContainer = toArray(container.querySelectorAll('.comparison__specs .swiper'));
		const specsSwipers = [];

		forEach(specsContainer, (item) => {
			const ins = new this.Swiper(item, {
				slidesPerView: 2,
				spaceBetween: 10,
				allowTouchMove: false,
				breakpoints: {
					868: { slidesPerView: 3 },
					992: { slidesPerView: 4 },
					1200: { slidesPerView: 5 },
					1440: { slidesPerView: 6 },
				},
			});
			specsSwipers.push(ins);
		});
		return specsSwipers;
	};

	getSectionSpecs = ({ offers, sectionId }) => {
    let allSpecs = {};
    const sectionSpecs = [];

    forEach(offers, ({ spec }) => {
      const offerSectionSpecs = get(spec, sectionId, {});
      forEach(offerSectionSpecs, ({ title, code, delimiter }) => {
        set(allSpecs, code, {
          code,
          title,
          delimiter,
          values: [],
        });
      });
    });
    forEach(offers, ({ spec }) => {
      const offerSectionSpecs = get(spec, sectionId, {});
      forEach(allSpecs, ({ code }, idx) => {
        const offerSpec = get(offerSectionSpecs, code, {});
        const value = {
          type: get(offerSpec, 'type'),
          value: get(offerSpec, 'value', null),
        };
        allSpecs[idx].values.push(value);
      });
    });

    forEach(values(allSpecs), ({ delimiter, title, values }, idx) => {
      if (delimiter) {
        sectionSpecs.push({
          delimiter: title,
          specs: [],
        });
      } else {
        if (idx === 0) {
          sectionSpecs.push({
            delimiter: '',
            specs: [],
          });
        }
        sectionSpecs[sectionSpecs.length - 1].specs.push({
          title: title,
          values: values,
        });
      }
    });
    return sectionSpecs;
	};

	getDifferentSpecs = (sectionSpecs) => {
    return reduce(sectionSpecs, (result, { delimiter, specs }) => {
      const newSpecs = filter(specs, ({ values }) => {
        return some(values, ({ value }) => !isEqual(value, values[0].value));
      });
      if (newSpecs.length)
        result.push({ delimiter, specs: newSpecs });
      return result;
    }, []);
	};

	/**
	 * Установка загрузчика на список
	 * @param target{HTMLElement|null} - элемент на которой вешать loader
	 * @param cb{Function} - колбек
	 * @return {Promise<void>}
	 */
	loading = async (target, cb) => {
		target && target.classList.add('loader');
		await new Promise((resolve) => {
			setTimeout(async () => {
				await cb();
				target && target.classList.remove('loader');
				resolve();
			}, 0);
		});
	};

  destroy = () => {
    if (this.block) {
      this.block.classList.remove('comparison_inited');
      delete this.block.Comparison;
    }
  };

  static init(options) {
    Array.from(document.querySelectorAll('.comparison:not(.comparison_inited)')).forEach((elem) => new Comparison(elem, options));
  }
}

if (NOT_PROD) {
	import('./data').then(({ data }) => {
		Comparison.init(data);
	});
}

// Экспорт;
window.Block.Comparison = Comparison;
